import React, { useState, useEffect } from "react";

const InventoryValuationCalculator = () => {
  const [quantity, setQuantity] = useState(1000);
  const [unitCost, setUnitCost] = useState(25);
  const [valuationMethod, setValuationMethod] = useState("fifo");
  const [markupPercentage, setMarkupPercentage] = useState(40);
  const [overheadCost, setOverheadCost] = useState(2000);
  const [currency, setCurrency] = useState("USD");
  const [depreciationRate, setDepreciationRate] = useState(10);
  const [result, setResult] = useState({
    totalValue: "0",
    unitValue: "0",
    retailValue: "0",
    netValue: "0",
    breakdown: "",
  });

  // Inventory Valuation Calculation
  const calculateInventory = () => {
    let qty = parseFloat(quantity);
    let cost = parseFloat(unitCost);
    let markup = parseFloat(markupPercentage);
    let overhead = parseFloat(overheadCost);
    let depreciation = parseFloat(depreciationRate);

    if (isNaN(qty) || qty <= 0 || isNaN(cost) || cost < 0 || isNaN(markup) || markup < 0 || isNaN(overhead) || overhead < 0 || isNaN(depreciation) || depreciation < 0) return;

    // Calculate base inventory value
    let baseValue = qty * cost;

    // Apply valuation method adjustments
    let valuationMultiplier = 1;
    let methodDescription = "";
    
    switch(valuationMethod) {
      case "fifo":
        valuationMultiplier = 1.0;
        methodDescription = "First-In-First-Out assumes oldest inventory is sold first";
        break;
      case "lifo":
        valuationMultiplier = 0.95;
        methodDescription = "Last-In-First-Out assumes newest inventory is sold first";
        break;
      case "weighted":
        valuationMultiplier = 0.98;
        methodDescription = "Weighted Average considers all inventory costs equally";
        break;
      case "specific":
        valuationMultiplier = 1.02;
        methodDescription = "Specific Identification tracks each item individually";
        break;
    }

    const adjustedValue = baseValue * valuationMultiplier;

    // Calculate per-unit overhead cost
    const overheadPerUnit = overhead / qty;
    const totalUnitCost = cost + overheadPerUnit;

    // Calculate depreciation
    const depreciationAmount = (adjustedValue * depreciation) / 100;
    const netInventoryValue = adjustedValue - depreciationAmount + overhead;

    // Calculate retail value with markup
    const retailPrice = totalUnitCost * (1 + markup / 100);
    const totalRetailValue = retailPrice * qty;

    // Format currency symbol
    const currencySymbols = {
      USD: "$",
      EUR: "€",
      GBP: "£",
      INR: "₹",
      JPY: "¥",
      AUD: "A$",
      CAD: "C$",
    };
    const symbol = currencySymbols[currency] || currency;

    const totalValueDisplay = `${symbol}${adjustedValue.toFixed(2)}`;
    const unitValueDisplay = `${symbol}${totalUnitCost.toFixed(2)}`;
    const retailValueDisplay = `${symbol}${totalRetailValue.toFixed(2)}`;
    const netValueDisplay = `${symbol}${netInventoryValue.toFixed(2)}`;

    let breakdown = `Your inventory valuation uses ${valuationMethod.toUpperCase()} method. Base value of ${qty} units at ${symbol}${cost} each equals ${symbol}${baseValue.toFixed(2)}. After applying ${valuationMethod.toUpperCase()} adjustment (${(valuationMultiplier * 100).toFixed(0)}%), the inventory value is ${totalValueDisplay}. Including overhead costs of ${symbol}${overhead.toFixed(2)} and ${depreciation}% depreciation (${symbol}${depreciationAmount.toFixed(2)}), the net inventory value is ${netValueDisplay}. ${methodDescription}.`;

    setResult({
      totalValue: totalValueDisplay,
      unitValue: unitValueDisplay,
      retailValue: retailValueDisplay,
      netValue: netValueDisplay,
      breakdown,
    });
  };

  useEffect(() => {
    calculateInventory();
  }, []);

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: "white" }}>
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
              <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
              <line x1="12" y1="22.08" x2="12" y2="12"></line>
            </svg>
          </div>
          <h1>Inventory Valuation Calculator</h1>
          <p>
            Calculate accurate inventory valuations using multiple accounting methods including FIFO, LIFO, and Weighted Average. Factor in overhead costs, depreciation, and markup for comprehensive inventory analysis.
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Inventory Details
              </h2>

              {/* Quantity Input */}
              <div className="input-group">
                <label>Quantity (Units)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">📦</span>
                  <input
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    className="calc-input"
                    placeholder="1000"
                    min="1"
                  />
                </div>
                <div className="quick-select-group">
                  {[100, 500, 1000, 5000, 10000].map((val) => (
                    <button
                      key={val}
                      onClick={() => setQuantity(val)}
                      className="quick-select-btn primary"
                    >
                      {val}
                    </button>
                  ))}
                </div>
              </div>

              {/* Unit Cost Input */}
              <div className="input-group">
                <label>Unit Cost ({currency})</label>
                <div className="input-wrapper">
                  <span className="input-prefix">💵</span>
                  <input
                    type="number"
                    value={unitCost}
                    onChange={(e) => setUnitCost(e.target.value)}
                    className="calc-input"
                    placeholder="25"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="quick-select-group">
                  {[10, 25, 50, 100, 200].map((h) => (
                    <button
                      key={h}
                      onClick={() => setUnitCost(h)}
                      className="quick-select-btn success"
                    >
                      {h}
                    </button>
                  ))}
                </div>
              </div>

              {/* Valuation Method */}
              <div className="input-group">
                <label>Valuation Method</label>
                <div className="quick-select-group">
                  {[
                    { value: "fifo", label: "FIFO" },
                    { value: "lifo", label: "LIFO" },
                    { value: "weighted", label: "Weighted Avg" },
                    { value: "specific", label: "Specific ID" },
                  ].map((method) => (
                    <button
                      key={method.value}
                      onClick={() => setValuationMethod(method.value)}
                      className={`quick-select-btn ${valuationMethod === method.value ? "primary" : "secondary"}`}
                    >
                      {method.label}
                    </button>
                  ))}
                </div>
                <p style={{ fontSize: "0.75rem", color: "#6b7280", marginTop: "0.5rem" }}>
                  FIFO: First-In-First-Out | LIFO: Last-In-First-Out | Weighted: Average Cost | Specific: Individual Item Tracking
                </p>
              </div>

              {/* Markup Percentage */}
              <div className="input-group">
                <label>Markup Percentage (%)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">📈</span>
                  <input
                    type="number"
                    value={markupPercentage}
                    onChange={(e) => setMarkupPercentage(e.target.value)}
                    className="calc-input"
                    placeholder="40"
                    min="0"
                    step="1"
                  />
                </div>
                <div className="quick-select-group">
                  {[20, 30, 40, 50, 100].map((val) => (
                    <button
                      key={val}
                      onClick={() => setMarkupPercentage(val)}
                      className="quick-select-btn success"
                    >
                      {val}%
                    </button>
                  ))}
                </div>
              </div>

              {/* Overhead Cost */}
              <div className="input-group">
                <label>Total Overhead Cost ({currency})</label>
                <div className="input-wrapper">
                  <span className="input-prefix">🏭</span>
                  <input
                    type="number"
                    value={overheadCost}
                    onChange={(e) => setOverheadCost(e.target.value)}
                    className="calc-input"
                    placeholder="2000"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="quick-select-group">
                  {[500, 1000, 2000, 5000, 10000].map((val) => (
                    <button
                      key={val}
                      onClick={() => setOverheadCost(val)}
                      className="quick-select-btn primary"
                    >
                      {val}
                    </button>
                  ))}
                </div>
              </div>

              {/* Depreciation Rate */}
              <div className="input-group">
                <label>Depreciation Rate (%)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">📉</span>
                  <input
                    type="number"
                    value={depreciationRate}
                    onChange={(e) => setDepreciationRate(e.target.value)}
                    className="calc-input"
                    placeholder="10"
                    min="0"
                    max="100"
                    step="1"
                  />
                </div>
                <div className="quick-select-group">
                  {[0, 5, 10, 15, 20].map((val) => (
                    <button
                      key={val}
                      onClick={() => setDepreciationRate(val)}
                      className="quick-select-btn success"
                    >
                      {val}%
                    </button>
                  ))}
                </div>
              </div>

              {/* Currency */}
              <div className="input-group">
                <label>Currency</label>
                <div className="quick-select-group">
                  {[
                    { value: "USD", label: "USD ($)" },
                    { value: "EUR", label: "EUR (€)" },
                    { value: "GBP", label: "GBP (£)" },
                    { value: "INR", label: "INR (₹)" },
                  ].map((curr) => (
                    <button
                      key={curr.value}
                      onClick={() => setCurrency(curr.value)}
                      className={`quick-select-btn ${currency === curr.value ? "primary" : "secondary"}`}
                    >
                      {curr.label}
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={calculateInventory} className="calculate-btn">
                Calculate Inventory Value
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M12 8v4l3 2"></path>
                </svg>
                Valuation Results
              </h2>

              <div className="result-card">
                {/* Total Inventory Value */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Total Inventory Value</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">{result.totalValue}</div>
                  <div className="result-subtext">current inventory worth</div>
                </div>

                {/* Unit Value */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Cost Per Unit</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">{result.unitValue}</div>
                  <div className="result-subtext">including overhead</div>
                </div>

                {/* Retail Value */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Total Retail Value</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">{result.retailValue}</div>
                  <div className="result-subtext">with markup applied</div>
                </div>

                {/* Net Value */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Net Book Value</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">{result.netValue}</div>
                  <div className="result-subtext">after depreciation</div>
                </div>

                {/* Breakdown */}
                <div className="breakdown-section" style={{ marginTop: "1rem" }}>
                  <h3 className="breakdown-title">Valuation Breakdown</h3>
                  <p style={{ fontSize: "0.9rem", color: "#065f46" }}>
                    {result.breakdown}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>Understanding Inventory Valuation</h3>
              <p>
                Inventory valuation is the accounting process of assigning monetary value to a company's inventory. It directly impacts cost of goods sold (COGS), profit margins, and balance sheet accuracy. Proper inventory valuation is crucial for financial reporting, tax compliance, and business decision-making.
                Different valuation methods (FIFO, LIFO, Weighted Average) can significantly affect reported profits and tax obligations. This calculator helps businesses determine accurate inventory values using industry-standard methods while accounting for overhead costs and depreciation.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>Valuation Methods Explained:</h4>
            <div className="formula-display">
              FIFO (First-In-First-Out): Oldest inventory sold first, values remaining at recent costs
            </div>
            <div className="formula-display" style={{ marginTop: "0.5rem" }}>
              LIFO (Last-In-First-Out): Newest inventory sold first, values remaining at older costs
            </div>
            <div className="formula-display" style={{ marginTop: "0.5rem" }}>
              Weighted Average: All inventory costs averaged together equally
            </div>
            <div className="formula-display" style={{ marginTop: "0.5rem" }}>
              Specific Identification: Each item tracked with its actual cost
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">Base Value</div>
                <div className="formula-param-value">Quantity × Unit Cost</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Overhead</div>
                <div className="formula-param-value">Storage, Handling, etc.</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Depreciation</div>
                <div className="formula-param-value">Obsolescence Adjustment</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Retail Value</div>
                <div className="formula-param-value">Cost + Markup %</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>Which valuation method should I use?</h5>
              <p>
                FIFO is most common and generally accepted. LIFO can reduce taxes during inflation but isn't allowed in some countries. Choose based on your industry, tax strategy, and regulatory requirements.
              </p>
            </div>

            <div className="faq-item">
              <h5>What are overhead costs in inventory?</h5>
              <p>
                Overhead costs include warehousing, utilities, insurance, handling, and other expenses related to storing and managing inventory. These should be allocated across your inventory units.
              </p>
            </div>

            <div className="faq-item">
              <h5>Why apply depreciation to inventory?</h5>
              <p>
                Depreciation accounts for obsolescence, damage, or loss of value over time. It's especially important for perishable goods, technology products, or fashion items that lose value quickly.
              </p>
            </div>

            <div className="faq-item">
              <h5>How does markup affect inventory valuation?</h5>
              <p>
                Markup shows potential retail value but doesn't change the book value of inventory. It's useful for understanding profit margins and pricing strategies alongside actual inventory costs.
              </p>
            </div>

            <div className="faq-item">
              <h5>How often should I value my inventory?</h5>
              <p>
                Most businesses conduct physical inventory counts quarterly or annually. However, continuous monitoring with inventory management systems provides more accurate, real-time valuations.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InventoryValuationCalculator;